import { Link } from "react-router-dom";
import { Mail, MapPin, Phone, Clock } from "lucide-react";
import { BUSINESS, CARRIERS } from "@/lib/site";
import { PLANS } from "@/lib/plans";
import { CITIES } from "@/lib/cities";
import { Logo } from "@/components/site/Logo";

export const Footer = () => {
  return (
    <footer
      data-testid="site-footer"
      className="bg-[var(--ink)] text-slate-200 mt-24"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          <div>
            <div className="inline-flex items-center bg-white rounded-2xl p-2 shadow-sm">
              <Logo className="h-16 w-auto" />
            </div>
            <p className="mt-5 text-sm text-slate-300 leading-relaxed">
              Independent insurance brokerage based on Florida’s Emerald Coast.
              Personalized health, Medicare and supplemental coverage with no obligation.
            </p>
            <p className="mt-5 eyebrow text-slate-400">{BUSINESS.experience}</p>
          </div>

          <div>
            <p className="eyebrow text-slate-400">Plans</p>
            <ul className="mt-5 space-y-3 text-sm">
              {PLANS.map((p) => (
                <li key={p.slug}>
                  <Link
                    to={`/${p.slug}`}
                    data-testid={`footer-link-${p.slug}`}
                    className="text-slate-300 hover:text-white transition-colors"
                  >
                    {p.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="eyebrow text-slate-400">Service Areas</p>
            <ul className="mt-5 space-y-3 text-sm">
              {CITIES.map((c) => (
                <li key={c.slug}>
                  <Link
                    to={`/${c.slug}`}
                    data-testid={`footer-city-${c.slug}`}
                    className="text-slate-300 hover:text-white transition-colors"
                  >
                    {c.city}, FL
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="eyebrow text-slate-400">Carriers</p>
            <ul className="mt-5 grid grid-cols-2 gap-x-3 gap-y-2 text-sm text-slate-300">
              {CARRIERS.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
          </div>

          <div>
            <p className="eyebrow text-slate-400">Visit / Call</p>
            <ul className="mt-5 space-y-3 text-sm text-slate-300">
              <li className="flex items-start gap-2">
                <MapPin className="w-4 h-4 mt-0.5 text-[var(--brand-secondary)]" />
                <span>{BUSINESS.address}</span>
              </li>
              <li className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-[var(--brand-secondary)]" />
                <a
                  data-testid="footer-phone-link"
                  href={`tel:${BUSINESS.phoneRaw}`}
                  className="hover:text-white"
                >
                  {BUSINESS.phone}
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-[var(--brand-secondary)]" />
                <a
                  data-testid="footer-email-link"
                  href={`mailto:${BUSINESS.email}`}
                  className="hover:text-white break-all"
                >
                  {BUSINESS.email}
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-[var(--brand-secondary)]" />
                <span>{BUSINESS.hours}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-slate-400">
          <p>
            © {new Date().getFullYear()} {BUSINESS.name}. Independent broker licensed in FL & CO.
            Not connected with or endorsed by the U.S. government or the federal Medicare program.
          </p>
          <p>
            We do not offer every plan available in your area. Currently we represent {CARRIERS.length} organizations.
            Please contact Medicare.gov, 1-800-MEDICARE, or your local State Health Insurance Program (SHIP)
            for information on all options.
          </p>
        </div>
        <p className="mt-4 text-xs text-slate-400" data-testid="footer-provider-disclaimer">
          Doctors and dentists can add and drop insurance carriers at any time. Always double-check with the provider’s
          office to make sure they are currently accepting the insurance before you make a purchase.
        </p>
      </div>
    </footer>
  );
};
